/** QuickTicketing — Cycle de vie d'un ticket (partagé serveur / navigateur).
 *
 *   nouveau ──(admin qualifie)──▶ à analyser ──▶ à redévelopper ──▶ en cours ──▶ résolu ──▶ clos
 *      │                              │                          (correctif livré : à tester)
 *      └──▶ infos demandées ◀─────────┘ (la réponse du demandeur le remet          └──▶ rouvert (à analyser)
 *      └──▶ sans suite                      dans l'état d'où il venait)
 *
 * Le demandeur dépose, répond, confirme ou rouvre. L'administrateur (et Claude, par
 * une clé d'API) fait tout le reste. La « file de Claude » = à analyser, à redévelopper,
 * en cours : ce qu'un administrateur a qualifié et qui attend un traitement.
 */

export const STATUSES = [
  { id: 'nouveau', label: 'Nouveau', hint: 'Déposé, en attente de qualification par un administrateur' },
  { id: 'en_attente', label: 'Infos demandées', hint: 'Une précision est attendue du demandeur' },
  { id: 'a_analyser', label: 'À analyser', hint: 'Qualifié : la cause est à rechercher' },
  { id: 'a_redevelopper', label: 'À redévelopper', hint: 'Cause connue : un correctif ou une évolution est à développer' },
  { id: 'en_cours', label: 'En cours', hint: 'Correctif en développement' },
  { id: 'a_tester', label: 'À tester', hint: 'Correctif livré : le demandeur le teste, puis confirme ou rouvre' },
  { id: 'resolu', label: 'Résolu', hint: 'Correctif livré : le demandeur confirme ou rouvre' },
  { id: 'clos', label: 'Clos', hint: 'Confirmé, soldé' },
  { id: 'rejete', label: 'Sans suite', hint: 'Doublon, pas un défaut, ou retiré par le demandeur' },
];
export const STATUS_IDS = STATUSES.map((s) => s.id);
export const statusLabel = (id) => STATUSES.find((s) => s.id === id)?.label ?? id;

export const CLOSED = ['clos', 'rejete'];
export const isOpen = (status) => !CLOSED.includes(status);
/** Correctif livré, en attente du retour du demandeur (confirmer → clos, ou rouvrir). */
export const DELIVERED = ['a_tester', 'resolu'];
/** Ce qu'un administrateur a qualifié et qui attend un traitement (file de Claude). */
export const QUEUE = ['a_analyser', 'a_redevelopper', 'en_cours'];

export const TYPES = [
  { id: 'bug', label: 'Dysfonctionnement' },
  { id: 'amelioration', label: 'Amélioration' },
  { id: 'question', label: 'Question' },
];
export const SEVERITIES = [
  { id: 'bloquant', label: 'Bloquant', hint: 'Impossible de travailler' },
  { id: 'genant', label: 'Gênant', hint: 'Un contournement existe' },
  { id: 'mineur', label: 'Mineur', hint: 'Détail, confort' },
];
export const PRIORITIES = [
  { id: 'P1', label: 'P1 — critique' },
  { id: 'P2', label: 'P2 — haute' },
  { id: 'P3', label: 'P3 — normale' },
  { id: 'P4', label: 'P4 — basse' },
];
export const labelOf = (list, id) => list.find((x) => x.id === id)?.label ?? id ?? '';

/**
 * Le demandeur peut-il faire passer son ticket de `from` à `to` ?
 *  - retirer un ticket pas encore pris en charge (nouveau / infos demandées → sans suite) ;
 *  - confirmer un correctif livré (à tester / résolu → clos) ;
 *  - rouvrir un correctif livré (à tester / résolu → à analyser, commentaire obligatoire côté serveur).
 * L'administrateur, lui, passe d'un état à n'importe quel autre.
 */
export function reporterMayMove(from, to) {
  if (to === 'rejete') return from === 'nouveau' || from === 'en_attente';
  if (DELIVERED.includes(from)) return to === 'clos' || to === 'a_analyser';
  return false;
}
